const logging = require('@sstdev/lib_logging');
const dataAccessMethods = require('../../../dataAccessMethods');
const Blockly = require('blockly');
const mdcConfig = require('@sstdev/lib_metadata-config');
const { config } = mdcConfig;
const blockGroupConfigurations = require('../../blockGroupConfigurations');

const setupAddCodeProperties = require('../../../helpers/addCodeProperties');
const convertToBoolean = require('../../../helpers/convertToBoolean');
const DelegatingMdcBlockGroup = require('../_DelegatingMdcBlockGroup');
const arrayFromCommaSeparatedValues = require('../../../helpers/arrayFromCommaSeparatedValues');

const hNodeTypes = require('./types');

class ListColumn extends DelegatingMdcBlockGroup {
    constructor(hNodeTypeGroup, categorySpec, color, subset = '') {
        super(hNodeTypeGroup, categorySpec, color, subset);
        super.types = hNodeTypes;
    }
    /**
     * @param {{[key: string]: any}} code
     * @param {import('blockly').Block} blocklyBlock
     * @param {string} hNodeType
     */
    addCodeGeneratorCustom(code, blocklyBlock, hNodeType) {
        if (this.types[hNodeType]?.convertCustomFieldsToCode) {
            const resultCode = this.types[hNodeType].convertCustomFieldsToCode(code, blocklyBlock);
            if (typeof resultCode === 'string') {
                logging.error(
                    `${hNodeType}.convertCustomFieldsToCode should not return a string. It should return a JSON object instead.`
                );
                return resultCode;
            }
            return JSON.stringify(resultCode);
        }

        const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);

        let textPropertyName = blocklyBlock.getFieldValue('propertyName');
        // Drop downs
        let dropdownNamespace;
        let dropdownRelation;
        if (['Calculated', 'ForeignRelationText'].includes(hNodeType)) {
            dropdownNamespace = blocklyBlock.getFieldValue('namespace');
            dropdownRelation = blocklyBlock.getFieldValue('relation');
        }
        // Compose property path if this is for a drop down/foreign key type data.
        let propertyName;
        if (hNodeType === 'ForeignRelationText') {
            code.namespace = dropdownNamespace;
            code.relation = dropdownRelation;
            code.displayProperties = arrayFromCommaSeparatedValues(textPropertyName);
            // for compatibility reasons, both with old UIs, and because e.g. headers and data-ids are based off of it,
            // we still set the propertyName:
            propertyName = `${dropdownNamespace}:${dropdownRelation}.${code.displayProperties[0]}`;
        } else if (hNodeType === 'Calculated') {
            // Consuming code will call the rules engine with
            // 'didGet_<namespace>_<relation>'
            code.namespace = dropdownNamespace;
            code.relation = dropdownRelation;
            propertyName = textPropertyName;
            // displayTypeDropdown
            const displayType = blocklyBlock.getFieldValue('displayType');
            code.dataType = getDataTypeFromBlockName(this.blockName(displayType));
            addCodeProperties([{ key: 'calculationDependencies', handler: arrayFromCommaSeparatedValues }]);
        } else {
            propertyName = textPropertyName;
        }

        if (hNodeType === 'GpsCoordinates') {
            addCodeProperties(['iconName']);
        }

        if (hNodeType === 'MappedBoolean') {
            addCodeProperties([
                { key: 'mappedTrueValue', value: 'trueValue' },
                { key: 'mappedFalseValue', value: 'falseValue' }
            ]);
        }

        if (hNodeType === 'PatchDetail') {
            propertyName = 'patches';
        }

        if (hNodeType === 'DateTime') {
            addCodeProperties(['format']);
        }

        code.label = code.title;
        code.propertyName = propertyName;
        if (hNodeType !== 'Calculated') {
            code.dataType = getDataTypeFromBlockName(this.blockName(hNodeType));
        }
        code.sequence = getSequenceOfBlockBasedOnBlockOrder(blocklyBlock);

        if (!['Thumbnail', 'GpsCoordinates', 'PatchDetail'].includes(hNodeType)) {
            addCodeProperties([{ key: 'sortable', handler: convertToBoolean }, 'visuallyFlagOnValue']);
        }

        if (hNodeType === 'Text') {
            addCodeProperties([{ key: 'wrapText', handler: convertToBoolean }]);
        }

        addCodeProperties([
            { key: 'hidden', handler: convertToBoolean },
            { key: 'bold', handler: convertToBoolean },
            { key: 'pinOnDesktop', handler: convertToBoolean },
            { key: 'pinOnMobile', handler: convertToBoolean }
        ]);

        return JSON.stringify(code);
    }

    /**
     * @param {unknown} color
     * @param {import('blockly').Block} blocklyBlock
     * @param {string} hNodeType
     */
    addToBlocksCustom(color, blocklyBlock, hNodeType, mdcBlock, topInput) {
        if (this.types[hNodeType]) {
            this.types[hNodeType].addBlockSpecificFields(color, blocklyBlock, mdcBlock, topInput);
            return;
        }
        if (['ForeignRelationText', 'Calculated'].includes(hNodeType)) {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Namespace:')
                .appendField(new Blockly.FieldDropdown(dataAccessMethods.getNamespaces), 'namespace');
            let blockId = blocklyBlock.id;
            let workspace = blocklyBlock.workspace;
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Relation:')
                .appendField(
                    new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId)),
                    'relation'
                );
        }

        if (hNodeType !== 'PatchDetail') {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Property Name:')
                .appendField(new Blockly.FieldTextInput('title'), 'propertyName');
        }
        if (hNodeType === 'Calculated') {
            // Exclude structural/non-data list columns that aren't valid as a Calculated display type.
            // Calculated renders a computed value using another column's display format — so only
            // data-display columns (Text, DateTime, Currency, etc.) belong here. Structural blocks
            // like EmbeddedList (nested array) and LineBreak (layout separator) don't display scalar values.
            const displayTypeDropdown = new Blockly.FieldDropdown(
                blockGroupConfigurations.listColumn.controls
                    .filter(c => !['Calculated', 'EmbeddedList', 'LineBreak'].includes(c))
                    .map(k => [k, k])
            );
            // Only show data formats dropdown if formats are needed (based on dataType)
            // For instance, string data types can have formats like email, long-text, date-time, etc.
            // but an integer is just an integer.
            blocklyBlock
                .appendDummyInput('displayType')
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField(' Display Type:')
                .appendField(
                    // first input is valid values for data type
                    // second input is the validation function which I've hijacked to display
                    // a second dropdown of data formats if necessary.
                    displayTypeDropdown,
                    'displayType'
                );
            blocklyBlock
                .appendDummyInput('calculationDependencies')
                .setAlign(Blockly.ALIGN_LEFT)
                .appendField(' Calculation Dependencies')
                .appendField(new Blockly.FieldTextInput(''), 'calculationDependencies');
            blocklyBlock.setTooltip(
                'A comma separated list of field property names that this calculation depends on (no spaces allowed).'
            );
        }

        if (hNodeType === 'Text') {
            blocklyBlock.appendDummyInput().setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Wrap text:')
                .appendField(new Blockly.FieldCheckbox('false'), 'wrapText');
        }

        if (hNodeType === 'MappedBoolean') {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  True Text:')
                .appendField(new Blockly.FieldTextInput('Yes'), 'trueValue');
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  False Text:')
                .appendField(new Blockly.FieldTextInput(''), 'falseValue');
        }
        if (hNodeType === 'GpsCoordinates') {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Icon name:')
                .appendField(new Blockly.FieldTextInput('navigation'), 'iconName');
        }
        if (!['Thumbnail', 'GpsCoordinates', 'PatchDetail'].includes(hNodeType)) {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Sortable:')
                .appendField(new Blockly.FieldCheckbox('false'), 'sortable');
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Flag row if value:')
                .appendField(new Blockly.FieldTextInput(''), 'visuallyFlagOnValue');
        }

        if (hNodeType === 'DateTime') {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Format:')
                .appendField(new Blockly.FieldDropdown(config.dateTimeFormatOptions), 'format');
        }
        blocklyBlock
            .appendDummyInput()
            .setAlign(Blockly.ALIGN_RIGHT)
            .appendField('  Hide Column By Default:')
            .appendField(new Blockly.FieldCheckbox('false'), 'hidden');

        blocklyBlock
            .appendDummyInput()
            .setAlign(Blockly.ALIGN_RIGHT)
            .appendField('  Display Column in Bold:')
            .appendField(new Blockly.FieldCheckbox('false'), 'bold')
            .appendField('  Pin On Desktop:')
            .appendField(new Blockly.FieldCheckbox('false'), 'pinOnDesktop')
            .appendField('  Pin On Mobile:')
            .appendField(new Blockly.FieldCheckbox('false'), 'pinOnMobile');
    }
}

/**
 * @param {string} blockName
 */
function getDataTypeFromBlockName(blockName) {
    switch (blockName) {
        case 'listColumn_EncodedText':
            return 'encodedText';
        case 'listColumn_MappedBoolean':
            return 'mappedBoolean';

        case 'listColumn_Barcode':
            return 'barcode';
        case 'listColumn_Text':
        case 'listColumn_ForeignRelationText':
        case 'listColumn_Link':
            return 'text';
        case 'listColumn_LocalVerboseDateTime':
            return 'localVerboseDateTime';
        case 'listColumn_Thumbnail':
            return 'base64';
        case 'listColumn_GpsCoordinates':
            return 'gpsCoordinates';
        case 'listColumn_PatchDetail':
            return 'patchDetail';
        case 'listColumn_Currency':
            return 'currency';
        case 'listColumn_Fraction':
            return 'fraction';
        case 'listColumn_DateTime':
            return 'dateTime';
        default:
            throw new Error(`Unknown list column block type: ${blockName}`);
    }
}

/**
 * @param {import('blockly').Block} block
 */
function getSequenceOfBlockBasedOnBlockOrder(block) {
    let parent = block.getSurroundParent();
    let parentChildren = parent.getDescendants();
    return (
        parentChildren.findIndex(function (value) {
            return value.id === block.id;
        }) - 1
    ); // subtract 1 because includes parent with getDescendants
}

module.exports = ListColumn;
